import { Component, EventEmitter, OnInit, Renderer2, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NoonReportsService, NoonReportV202005 } from '../../services/noon-reports.service';
import { ActivatedRoute, Router } from '@angular/router';
import { DateRange } from '../../../../shared/components/date-range.component';
import { Vessel } from '../../vessels.service';
import { DocumentService } from '../../../../documents/documents.service';
import { MessageLevel, PgMessagesService } from '../../../../shared/services/pg-messages.service';
import moment from 'moment';
import { DateTimeHelpers } from '../../../../shared/helpers/date-time.helpers';
import { saveAs } from 'file-saver';
import _ from 'lodash';
import {
  PgDateRangeTitleComponent,
  PgDateRangeTitleEnum, PgTitleComponent
} from '../../../../shared/components/title-bar/title-bar.component';
import { SharedModule } from 'src/app/shared/shared.module';
import { AuthenticationModule } from 'src/app/authentication/authentication.module';
import { AgGridModule } from 'ag-grid-angular';
import { GridOptions, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import { AgGridService } from 'src/app/shared/ag-grid/ag-grid.service';
import { KeycloakService } from 'src/app/authentication/keycloak.service';
import { UserLocalStoreService } from 'src/app/shared/services/user-local-store.service';
import { NoonReportsToolbarComponent } from 'src/app/transportation/vessels/noon-reports/noon-reports-v202005/noon-reports-v202005.header.component';

@Component({
  selector: 'app-noon-reports-v202005',
  templateUrl: './noon-reports-v202005.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SharedModule, AgGridModule, AuthenticationModule]
})
export class NoonReportsV202005Component implements OnInit, PgTitleComponent, PgDateRangeTitleComponent {

  title = new EventEmitter<string>();
  dateRangeSubTitle = new EventEmitter<[DateRange, PgDateRangeTitleEnum]>();
  @ViewChild(NoonReportsToolbarComponent)
    toolbarComponent!: NoonReportsToolbarComponent;
  toolbarComponentType = NoonReportsToolbarComponent;
  dateRange: DateRange;
  reports!: NoonReportV202005[];
  vessels!: Vessel[];
  options: any;

  gridOptions: GridOptions = {};
  gridRestored: boolean = false;

  constructor(private _activatedRoute: ActivatedRoute,
    private _noonReportsService: NoonReportsService,
    private _documentsService: DocumentService,
    private _pgMessagesService: PgMessagesService,
    private _router: Router,
    private _renderer: Renderer2,
    private _keycloakService: KeycloakService,
    private _userLocalStorage: UserLocalStoreService) {
    this.dateRange = {
      startDate: moment().startOf('day').subtract(7, 'd').toDate(),
      endDate: moment().endOf('day').toDate()
    };
  }

  columnDefs = [
    {
      headerName: 'Actions',
      cellRenderer: (params:ICellRendererParams) => {
        const container = this._renderer.createElement('div');
        if (params.data.fileId) {
          const fileName = `${params.data.vesselName} - ${DateTimeHelpers.formatLocalDateTime(params.data.date.timestamp)}`;
          const downloadBtn = AgGridService.downloadButton(
            this._renderer,
            () => this.downloadFile(params.data.fileId, fileName),
          );
          this._renderer.appendChild(container, downloadBtn);
        }
        if (this.hasRole('admin-editor')) {
          const deleteBtn = AgGridService.deleteButton(
            this._renderer,
            this._router,
            (_router, data) => this.delete(data),
            params.data
          );
          this._renderer.appendChild(container, deleteBtn);
        }
        return container;
      }
    },
    { headerName: 'Vessel', field: 'vesselName' },
    {
      headerName: 'Date (UTC)', field: 'date',
      cellRenderer: (row: ICellRendererParams) => this.formatDate(row.value.timestamp),
      sort: 'desc'
    },
    { headerName: 'Voyage Number', field: 'voyageDetails.voyageNumber' },
    { headerName: 'Type', field: 'reportType' },
    { headerName: 'From', field: 'voyageDetails.source' },
    { headerName: 'To', field: 'voyageDetails.destination' },
    { headerName: 'ETA', field: 'voyageDetails.eta', cellRenderer: (row: ICellRendererParams) => this.formatDate(row.data.voyageDetails.eta) },
    { headerName: 'Speed', field: 'speed.speed' },
    { headerName: 'Laden/Ballast', field: 'voyageDetails.isLaden' },
    { headerName: 'Distance to go', field: 'voyageDetails.distanceToGo' },
    {
      headerName: 'Cargo',
      cellRenderer: (row: ICellRendererParams) => this.formatCargoOnboard(row.data)
    },
    { headerName: 'Master', field: 'master' },
  ];

  ngOnInit(): void {
    this.gridOptions = AgGridService.getOptions();
    this.title.emit('Noon reports V202005');
    this._activatedRoute.queryParams.subscribe(p => {
      const startDate = +p['startTime'];
      const endDate = +p['endTime'];
      this.dateRange = {
        startDate: new Date(startDate),
        endDate: new Date(endDate)
      };
      this.dateRangeSubTitle.emit([this.dateRange, PgDateRangeTitleEnum.defaultTitle]);
      this.getReport();
    });
    this.getParameters();
    this.toolbarComponent.date.subscribe(dateRange => {
      this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this._router
          .navigate(['/vessels/noon-reports/v202005'], {
            queryParams: {
              startTime: dateRange?.startDate.getTime(),
              endTime: dateRange?.endDate.getTime()
            }
          })
          .then((e) => console.log(`Result of Navigation is ${e}`));
      });
    });
    this.toolbarComponent.downloadExcelReport.subscribe(() => {
      this._noonReportsService.downloadReports(this.dateRange.startDate.getTime(), this.dateRange.endDate.getTime()).subscribe((reports) => {
        saveAs(reports.blob, reports.fileName);
      });
    });
  }

  gridSetting(event: GridReadyEvent) {
    AgGridService.setting(event);
  }

  saveGridState(event: GridReadyEvent) {
    AgGridService.saveGridState(event, this.gridRestored, this._userLocalStorage, 'noonReport2005Grid');
  }

  restoreGridState(event: GridReadyEvent) {
    this.gridRestored = AgGridService.restoreGridState(event, this._userLocalStorage, 'noonReport2005Grid');
  }

  getParameters() {
    this._activatedRoute.queryParams.subscribe(p => {
      const startDate = +p['startTime'];
      const endDate = +p['endTime'];
      this.dateRange = {
        startDate: new Date(startDate),
        endDate: new Date(endDate)
      };
      this.toolbarComponent.dateRange = this.dateRange;
      this.dateRangeSubTitle.emit([this.dateRange, PgDateRangeTitleEnum.defaultTitle]);
    });
  }

  getReport() {
    this._noonReportsService.getNoonReportsV202005Between(this.dateRange).subscribe(reports => {
      this.reports = reports.sort((r1, r2) => r2.date!.timestamp - r1.date!.timestamp);
    });
  }

  formatDate(timestamp: number) {
    if (timestamp > 0) {
      return DateTimeHelpers.formatUTCDate(timestamp);
    } else {
      return '';
    }
  }

  downloadReport() {
    this._noonReportsService.downloadReports(this.dateRange.startDate.getTime(),
      this.dateRange.endDate.getTime()).subscribe(b => {
      saveAs(b.blob, `${DateTimeHelpers.formatLocalDate(this.dateRange.startDate.getTime())}-to-` +
          `${DateTimeHelpers.formatLocalDate(this.dateRange.endDate.getTime())}-reports.xlsx`);
    }, () => {
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Error,
        message: 'Could not download noon reports',
        topic: 'Voyages report'
      });
    });
  }

  delete(n: NoonReportV202005) {
    if (confirm('Are you sure you want to delete this item?')) {
      n.isDeleted = true;
      this._noonReportsService.deleteNoonV202005(n).subscribe(
        () => {
          this.getReport();
          this._pgMessagesService.publishMessage({
            level: MessageLevel.Info,
            topic: 'Noon Report Item',
            message: `${n.vesselName} item Removed`,
          });

          this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this._router.navigate(['/vessels/noon-reports/v202005'], {
              queryParams: {
                startTime: this.dateRange?.startDate.getTime(),
                endTime: this.dateRange?.endDate.getTime(),
              },
            });
          });
        }
      );
    }
  }

  downloadFile(fileId: string, prefix: string) {
    this._documentsService.download(fileId).subscribe((b) => {
      saveAs(b.body, `${prefix}-report.xlsx`);
    }, () => {
      this._pgMessagesService.publishMessage({
        message: 'Could not download report.',
        level: MessageLevel.Error,
        topic: 'Vessel report'
      });
    });
  }

  formatCargoOnboard(report: NoonReportV202005) {
    return _.chain(report.tanksCondition)
      .groupBy(r => r.product)
      .map((items, key) => `${key}: ${Math.round(items.map(i => i.quantity).reduce((p, c) => p + c, 0))}`)
      .value().join(', ');
  }

  hasRole(roleName: string) {
    return this._keycloakService.hasRole(roleName);
  }
}
